<script lang="ts">
// Packages
import { defineComponent, nextTick, type PropType } from 'vue';
import { liteClient } from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

// Directives
import dropdown, { closeDropdown } from '@vue-directives/dropdown';

// Styles (for dev)
// import 'instantsearch.css/themes/reset.css';
// import 'instantsearch.css/themes/satellite.css';

// Components
import {
  AisClearRefinements,
  AisConfigure,
  AisCurrentRefinements,
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisSortBy,
  AisStats,
  AisStateResults,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';
import SearchPagination from '@vue-components/SearchPagination.vue';

type RefinementList = { [key: string]: string[] };
type SortOption = {
  value: string;
  label: string;
};

type UiState = {
  [indexName: string]: {
    refinementList?: RefinementList;
    page?: number;
    query?: string;
    sortBy?: string;
  };
};
type UiRoute = Record<string, string /* | string[] | number */>;

const router = historyRouter<UiRoute>();

type Refinement = {
  label: string;
  attribute: string;
};

export default defineComponent({
  components: {
    AisClearRefinements,
    AisConfigure,
    AisCurrentRefinements,
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisSortBy,
    AisStats,
    AisStateResults,
    RefinementPanel,
    SearchPagination,
  },
  directives: {
    dropdown,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
    refinements: {
      type: Array as PropType<Refinement[]>,
      required: true,
    },
    sortOptions: {
      type: Array as PropType<SortOption[]>,
      required: false,
      default: () => [],
    },
    showSearchBox: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      searchClient: liteClient(this.appId, this.searchApiKey),
      userLat: null as number | null,
      userLng: null as number | null,
      locationQuery: null as string | null,
      useCurrentLocation: false,
      hasLocationError: false,
    };
  },
  computed: {
    defaultSortIndex() {
      return this.sortOptions[0]?.value ?? this.indexName;
    },
    routing() {
      const { indexName, defaultSortIndex, refinements, sortOptions } = this;
      return {
        router,
        stateMapping: {
          stateToRoute(uiState: UiState) {
            const indexUiState = uiState[indexName];
            const route: UiRoute = {};
            const { refinementList, page, query, sortBy } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = (refinementList[key] as Array<string>).join('~');
                }
              });
            }
            if (query) {
              route.query = query;
            }
            if (page && page > 1) {
              route.page = page.toString();
            }
            if (sortBy && sortBy !== defaultSortIndex) {
              route.sort = sortBy.slice(indexName.length + 1);
            }
            return route;
          },
          routeToState(routeState: UiRoute): UiState {
            const prefixedSortBy = routeState.sort ? `${indexName}_${routeState.sort}` : null;
            const sortBy =
              prefixedSortBy &&
              sortOptions.find((sortOption) => sortOption.value === prefixedSortBy)
                ? prefixedSortBy
                : null;

            const refinementList = Object.fromEntries(
              refinements
                .map(({ attribute }) => [
                  attribute,
                  routeState[attribute] ? routeState[attribute].split('~') : undefined,
                ])
                .filter(([, val]) => val !== undefined),
            );

            return {
              [indexName]: {
                query: routeState.query,
                refinementList,
                page: Math.max(Number(routeState.page) || 1, 1),
                sortBy: sortBy ?? defaultSortIndex,
              },
            };
          },
        },
      };
    },
  },
  methods: {
    closeDropdown,
    // The pre-JS listing (c-vacancies-search-ssr.twig) sits outside this app. Drop it
    // only once we have results of our own, so the listing never blanks out.
    removeSsrPlaceholder() {
      document.getElementById('search-index-ssr')?.remove();
    },
    onPageChange() {
      nextTick(() => this.$el.scrollIntoView());
    },
  },
});
</script>
<template>
  <ais-instant-search
    :search-client="searchClient"
    :routing="routing"
    :index-name="indexName"
    class="mx-auto container-304"
  >
    <ais-configure :hits-per-page.camel="15" />

    <!-- ====== START REFINEMENTS ====== -->
    <div class="mb-16">
      <div
        class="flex flex-col gap-x-6 gap-y-8 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center"
      >
        <refinement-panel
          v-for="refinement in refinements"
          :key="refinement.label"
          class="lg:basis-101"
          v-bind="refinement"
        />
        <!-- ====== START SEARCH BOX ====== -->
        <ais-search-box
          v-if="showSearchBox"
          class="lg:basis-101"
          :class-names="{
            'ais-SearchBox-form':
              'flex text-body-md gap-x-3 min-h-10 border-b-[1.5px] border-secondary-500 px-2 has-[.ais-SearchBox-input:focus-visible]:outline-2 has-[.ais-SearchBox-input:focus-visible]:outline-offset-2 has-[.ais-SearchBox-input:focus-visible]:outline-primary-300',
            'ais-SearchBox-input': 'grow outline-none placeholder:text-neutral-400',
            'ais-SearchBox-submit': '',
            'ais-SearchBox-reset': '',
          }"
          placeholder="Search..."
        >
          <template #submit-icon>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="text-[#1e201e]">
              <path
                d="M14.75 14.75L10.0833 10.0833M11.6389 6.19444C11.6389 9.20133 9.20133 11.6389 6.19444 11.6389C3.18756 11.6389 0.75 9.20133 0.75 6.19444C0.75 3.18756 3.18756 0.75 6.19444 0.75C9.20133 0.75 11.6389 3.18756 11.6389 6.19444Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </template>
        </ais-search-box>
        <!-- ====== END SEARCH BOX ====== -->
      </div>

      <!-- ====== START CURRENT REFINEMENTS ====== -->
      <ais-current-refinements
        class="mt-10 flex items-center justify-center gap-4 gap-x-8"
        :class-names="{
          'ais-CurrentRefinements--noRefinement': 'hidden',
        }"
      >
        <template #default="{ items /* , createURL */ }">
          <ul class="flex flex-wrap justify-center gap-x-6 gap-y-2 md:gap-y-6">
            <template v-for="item in items" :key="item.attribute">
              <li
                v-for="refinement in item.refinements"
                :key="
                  [
                    refinement.attribute,
                    refinement.type,
                    refinement.value,
                    refinement.operator,
                  ].join(':')
                "
                class="btn btn-primary flex items-center gap-x-3 btn-md"
              >
                {{ refinement.label }}
                <button
                  class="grid size-3.5 cursor-pointer place-items-center"
                  title="Remove this refinement"
                  type="button"
                  @click.prevent="item.refine(refinement)"
                >
                  <svg height="14" width="14" viewBox="0 0 13 13" aria-hidden="true">
                    <line
                      x1="0.6"
                      y1="0.6"
                      x2="12.4"
                      y2="12.4"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <line
                      x1="0.6"
                      y1="12.4"
                      x2="12.4"
                      y2="0.6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </li>
            </template>
          </ul>
          <ais-clear-refinements
            class=""
            :class-names="{
              'ais-ClearRefinements-button': 'btn btn-secondary btn-md flex items-center gap-x-3',
              'ais-ClearRefinements-button--disabled': 'hidden',
            }"
          >
            <template #resetLabel>Clear filters</template>
          </ais-clear-refinements>
        </template>
      </ais-current-refinements>
      <!-- ====== END CURRENT REFINEMENTS ====== -->
    </div>
    <!-- ====== END REFINEMENTS ====== -->

    <div>
      <div class="mb-10 flex items-center gap-x-10">
        <!-- ====== START SORT BY ====== -->
        <ais-sort-by
          v-if="sortOptions.length"
          v-dropdown
          :items="sortOptions"
          class="dropdown group relative"
        >
          <template #default="{ items, currentRefinement, refine }">
            <button
              aria-expanded="false"
              class="dropdown-trigger btn btn-primary flex items-center gap-x-3 btn-sm text-label-md"
              type="button"
            >
              Sort by
              <svg
                class="shrink-0"
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M1 1L6 6L11 1"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <ul
              class="absolute top-full left-0 z-1 mt-2 hidden w-max max-w-176 min-w-60 overflow-hidden rounded-lg bg-white text-body-md shadow-md group-[.open]:block"
            >
              <li
                v-for="item in items"
                :key="item.value"
                :aria-current="currentRefinement === item.value"
              >
                <button
                  ref="sortButtons"
                  :data-sort="item.value"
                  class="block w-full cursor-pointer px-4 py-3 text-left -outline-offset-4 transition-colors duration-300"
                  :class="[
                    currentRefinement === item.value ? 'bg-primary-200' : 'hover:bg-neutral-200',
                  ]"
                  type="button"
                  @click="
                    (refine(item.value),
                    closeDropdown(($event.target as HTMLElement).closest('.dropdown')!))
                  "
                >
                  {{ item.label }}
                </button>
              </li>
            </ul>
          </template>
        </ais-sort-by>
        <!-- ====== END SORT BY ====== -->
        <!-- ====== START STATS ====== -->
        <ais-stats class="ml-auto text-label-md">
          <template #default="{ results: { hits } }">
            <ais-state-results>
              <template #default="{ nbHits }">
                <!-- This slot only renders once Algolia has responded — see removeSsrPlaceholder. -->
                <span @vue:mounted="removeSsrPlaceholder">
                  Showing {{ hits.length }} of {{ nbHits }} results
                </span>
              </template>
            </ais-state-results>
          </template>
        </ais-stats>
        <!-- ====== END STATS ====== -->
      </div>

      <!-- ====== START HITS ====== -->
      <ais-hits>
        <template #default="{ items }">
          <ol v-if="items.length" class="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            <li v-for="item in items" :key="item.objectID" class="max-lg:max-w-85">
              <slot name="item" :item="item" />
            </li>
          </ol>
          <div v-else>
            <h3 class="mb-4 font-sweet-sans text-h4">Sorry, no vacancies found</h3>
            <p class="mb-6 max-w-prose text-body-md">
              We couldn't find any vacancies within your search. If you're looking for a particular
              role in a certain location, then please get in touch as we still may be able to help.
            </p>
            <a href="/contact" class="btn btn-primary">Get in touch</a>
          </div>
        </template>
      </ais-hits>
      <!-- ====== END HITS ====== -->

      <!-- ====== START PAGINATION ====== -->
      <search-pagination @page-change="onPageChange"></search-pagination>
      <!-- ====== END PAGINATION ====== -->
    </div>
  </ais-instant-search>
</template>
